import classNames from 'classnames';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import HeroesIcon from 'app/components/HeroesIcon';
import KDA from 'app/components/KDA';
import LoLPlayerRoleIndicator from 'app/components/LoLPlayerRoleIndicator';
import StatValue from 'app/components/StatValue';
import TeamName from 'app/components/TeamName';
import Tooltip from 'app/components/Tooltip';
import { LOL_PLAYERS_ROLE_ORDER } from 'app/config/videogameSpecific.config';
import { partitionArray } from 'app/utils/array';
import { getStatWinner } from 'app/utils/entities/match_data';
import StatWinnerArrow from 'app/components/StatWinnerArrow';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';

import { getLolPlayerStatsLines } from './utils';
import style from './style.module.scss';

interface Props {
  data: PlayersStatsBlockMatch;
}

const LolPlayers: FC<Props> = ({ data }) => {
  const { t } = useTranslation();

  const [selectedRole, setSelectedRole] = useState<LolPlayerRole>('top');

  const playersByRoles = getLolPlayerStatsLines(data);

  const displayedRole = playersByRoles[selectedRole];
  const [statLines, [heroesLine]] = partitionArray(displayedRole.stats, stat =>
    stat.display === 'hero' ? 1 : 0
  );

  return (
    <BlockTemplate
      title={t('blocks_players-stats_title')}
      withPeriodSelector
      containerClassname={style.playersRolesStatsBlockContent}
      hasData={data.opponents?.some(
        opponent => Object.values(opponent?.stats?.players || [])?.filter(Boolean).length
      )}
    >
      <TeamName.Container className={style.teamsNames}>
        <TeamName team={data.opponents.at(0)} logoStyle={style.teamNameLogoSize} />
        <TeamName
          team={data.opponents.at(1)}
          logoStyle={style.teamNameLogoSize}
          orientation="reversed"
        />
      </TeamName.Container>

      <div className={style.playersContainer}>
        <div className={style.roleSelection}>
          {LOL_PLAYERS_ROLE_ORDER.map(role => (
            <button
              key={role}
              onClick={() => setSelectedRole(role)}
              className={classNames(style.roleButton, { [style.selected]: selectedRole === role })}
            >
              <LoLPlayerRoleIndicator role={role} />
              <span>{t(`blocks_players-stats_videogame_lol_button_${role}`)}</span>
            </button>
          ))}
        </div>

        <div className={style.statsContainer}>
          <div>
            <div className={style.playerName}>
              {displayedRole.team1Player?.name ?? t('all-unknown')}
            </div>
            <HeroesIcon
              hidePercentage
              heroes={(heroesLine?.team1Value as HeroStat[]) ?? []}
              iconClassName={style.playerHero}
              placeholderSize={3}
              videogame="lol"
            />
          </div>
          <ul className={style.statLines}>
            <li className={classNames(style.statLine, style.statLineKDA)}>
              <span className={style.statTeam1Value}>
                <KDA
                  playerStats={
                    (
                      data?.opponents.at(0)?.stats?.players as Record<
                        LolPlayerRole,
                        StatPlayerLol | null
                      >
                    )?.[selectedRole] ?? {}
                  }
                />
              </span>
              <span />
              <Tooltip
                content={t('blocks_players-stats_videogame_lol_kda_tooltip')}
                className={style.statLineName}
              >
                {t('blocks_players-stats_videogame_lol_kda_title')}
              </Tooltip>
              <span />
              <span className={style.statTeam2Value}>
                <KDA
                  playerStats={
                    (
                      data?.opponents.at(1)?.stats?.players as Record<
                        LolPlayerRole,
                        StatPlayerLol | null
                      >
                    )?.[selectedRole] ?? {}
                  }
                />
              </span>
            </li>
            {statLines.map(statLine => {
              const key = `blocks_players-stats_videogame_lol_${statLine.name}`;
              const [isTeam1StatWinner, isTeam2StatWinner] = getStatWinner(statLine);

              return (
                <li key={statLine.name} className={style.statLine}>
                  <span
                    className={classNames(style.statTeam1Value, {
                      [style.statTeamValueWinner]: isTeam1StatWinner,
                      [style.statTeamValueLoser]: isTeam2StatWinner
                    })}
                  >
                    <StatValue
                      value={statLine.team1Value}
                      displayFunction={statLine.displayFunction}
                    />
                  </span>
                  <span>{isTeam1StatWinner && <StatWinnerArrow direction="left" />}</span>
                  <Tooltip content={t(`${key}_tooltip`)} className={style.statLineName}>
                    {t(`${key}_title`)}
                  </Tooltip>
                  <span>{isTeam2StatWinner && <StatWinnerArrow direction="right" />}</span>
                  <span
                    className={classNames(style.statTeam2Value, {
                      [style.statTeamValueWinner]: isTeam2StatWinner,
                      [style.statTeamValueLoser]: isTeam1StatWinner
                    })}
                  >
                    <StatValue
                      value={statLine.team2Value}
                      displayFunction={statLine.displayFunction}
                    />
                  </span>
                </li>
              );
            })}
          </ul>
          <div>
            <div className={style.playerNameReversed}>{displayedRole.team2Player?.name}</div>
            <HeroesIcon
              hidePercentage
              heroes={(heroesLine?.team2Value as HeroStat[]) ?? []}
              iconClassName={style.playerHero}
              orientation="reversed"
              placeholderSize={3}
              videogame="lol"
            />
          </div>
        </div>
      </div>
    </BlockTemplate>
  );
};

LolPlayers.displayName = 'LolPlayers';

export default LolPlayers;
